import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Download, ShieldCheck, Smartphone, RefreshCw, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { MyShowLogoFull } from '@/components/Logo';

interface Manifest {
  versionName: string;
  versionCode: number;
  buildId: string;
  gitCommit?: string;
  finishedAt?: string;
  fileName: string;
  latestName: string;
  sha256: string;
  sizeBytes: number;
  easArtifact?: string;
  generatedAt?: string;
}

const APK_BASE = (import.meta.env.VITE_TV_APK_BASE_URL as string | undefined)?.replace(/\/$/, '') || '/download';

function formatSize(bytes?: number) {
  if (!bytes) return '—';
  const mb = bytes / (1024 * 1024);
  return `${mb.toFixed(1)} MB`;
}

function formatDate(iso?: string) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('pt-BR');
  } catch {
    return iso;
  }
}

export function DownloadPage() {
  const [manifest, setManifest] = useState<Manifest | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetch(`${APK_BASE}/manifest.json`, { cache: 'no-store' })
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then(setManifest)
      .catch(() => setError('Não foi possível carregar os metadados do build.'));
  }, []);

  const apkHref = manifest ? `${APK_BASE}/${manifest.latestName}` : `${APK_BASE}/myshow-tv-latest.apk`;

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-background/80 backdrop-blur sticky top-0 z-10">
        <div className="container-app flex items-center justify-between h-16">
          <Link to="/" className="flex items-center">
            <MyShowLogoFull height={36} />
          </Link>
          <nav className="flex items-center gap-6 text-sm">
            <Link to="/pricing" className="text-muted-foreground hover:text-foreground">
              Planos
            </Link>
            <Link to="/login" className="text-muted-foreground hover:text-foreground">
              Entrar
            </Link>
            <Button asChild size="sm">
              <Link to="/cadastro">Criar minha TV grátis</Link>
            </Button>
          </nav>
        </div>
      </header>

      <section className="container-app py-16 lg:py-24">
        <div className="max-w-3xl mx-auto">
          <div className="text-center">
            <div className="inline-flex items-center gap-2 rounded-full bg-brand-100 text-brand-800 px-4 py-1.5 text-sm font-medium mb-6">
              <Smartphone className="h-4 w-4" />
              App MyShow TV · Android TV / TV Box
            </div>
            <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight">
              Baixe o app{' '}
              <span className="bg-gradient-to-r from-brand-600 to-accent bg-clip-text text-transparent">
                direto aqui
              </span>
            </h1>
            <p className="mt-6 text-lg text-muted-foreground max-w-2xl mx-auto">
              Se a Play Store não exibe o MyShow no seu modelo de TV, instale pelo arquivo
              abaixo. É o mesmo app, assinado pela Visual Software, e se mantém atualizado
              automaticamente.
            </p>
          </div>

          <div className="mt-12 rounded-2xl border border-border bg-card p-8 shadow-sm">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
              <div>
                <p className="text-sm text-muted-foreground">Versão mais recente</p>
                <p className="text-2xl font-bold mt-1">
                  {manifest ? `${manifest.versionName} (build #${manifest.versionCode})` : 'Carregando…'}
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  Publicado em {formatDate(manifest?.finishedAt)} · {formatSize(manifest?.sizeBytes)}
                </p>
              </div>
              <Button asChild size="lg" variant="gradient">
                <a href={apkHref} download>
                  <Download className="h-4 w-4" />
                  Baixar APK
                </a>
              </Button>
            </div>

            {manifest && (
              <div className="mt-6 pt-6 border-t border-border grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs text-muted-foreground">
                <div>
                  <span className="font-medium text-foreground">SHA-256:</span>{' '}
                  <code className="font-mono break-all">{manifest.sha256}</code>
                </div>
                {manifest.gitCommit && (
                  <div>
                    <span className="font-medium text-foreground">Commit:</span>{' '}
                    <code className="font-mono">{manifest.gitCommit.slice(0, 12)}</code>
                  </div>
                )}
              </div>
            )}

            {error && (
              <p className="mt-4 text-sm text-destructive">{error}</p>
            )}
          </div>

          <div className="mt-12">
            <h2 className="text-xl font-bold mb-6">Como instalar na TV</h2>
            <ol className="space-y-4">
              {[
                'Na TV, abra Configurações → Apps → Segurança e habilite "Fontes desconhecidas" para o navegador ou gerenciador de arquivos.',
                'Acesse esta página direto pela TV — ou baixe o APK no PC e transfira por pendrive / gerenciador de arquivos.',
                'Abra o arquivo .apk pelo gerenciador de arquivos da TV e confirme a instalação.',
                'Pronto. O app atualiza sozinho a cada abertura. Só baixe um novo APK quando avisarmos aqui.',
              ].map((step, i) => (
                <li key={i} className="flex gap-4">
                  <span className="flex-none flex items-center justify-center h-7 w-7 rounded-full bg-brand-600 text-white text-sm font-bold">
                    {i + 1}
                  </span>
                  <span className="text-muted-foreground pt-0.5">{step}</span>
                </li>
              ))}
            </ol>
          </div>

          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="rounded-xl border border-border p-5">
              <ShieldCheck className="h-6 w-6 text-brand-600 mb-3" />
              <h3 className="font-semibold">Assinado pela Visual Software</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Mesmo certificado da versão da Play Store. Se já estava instalado pela loja, o
                APK instala por cima sem perder pareamento.
              </p>
            </div>
            <div className="rounded-xl border border-border p-5">
              <RefreshCw className="h-6 w-6 text-brand-600 mb-3" />
              <h3 className="font-semibold">Atualização automática</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Mudanças de conteúdo, layout e funcionalidades chegam sozinhas via Expo Updates
                a cada vez que o app é aberto.
              </p>
            </div>
          </div>

          <div className="mt-12 text-center">
            <Link to="/" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
              Voltar pro site <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
